import React, { createContext, useContext, useState, useEffect } from "react";
import { trackEvent, toGaItem } from "../lib/analyticsEvents";

const CartContext = createContext(undefined);

// ── Helpers ────────────────────────────────────────────────────────

/**
 * Clé unique d'un item dans le panier.
 * - Produit simple  : "<product_id>::main"
 * - Avec variante   : "<product_id>::<variant_id>"
 */
const cartKey = (product_id, variant_id) =>
  `${product_id}::${variant_id || "main"}`;

/**
 * Construit un item de panier à partir d'un produit + variante optionnelle.
 * Snapshot du prix, du nom, etc. — comme ça un changement en DB ne modifie
 * pas les items déjà dans le panier.
 */
const buildCartItem = (product, quantity, variant) => {
  const effectivePrice = variant?.price ?? product.price;
  const effectiveStock =
    variant?.stock !== undefined ? variant.stock : product.stock;

  return {
    // Clé unique locale
    cart_item_id: cartKey(product.id, variant?.id),

    // Références DB (à envoyer au checkout)
    product_id: product.id,
    variant_id: variant?.id || null,

    // Snapshot affichage
    name: product.name,
    variant_name: variant?.name || null,
    slug: product.slug,
    variant_slug: variant?.slug || null,
    category_slug: product.category_slug || product.category || null,
    image_url: product.image_url || "",

    // Snapshot prix / stock
    price: effectivePrice,
    stock: effectiveStock,

    // Options
    quantity,
    notes: null,
  };
};

/**
 * Migration douce : le vieux format n'avait pas de cart_item_id.
 * On le régénère à partir de l'id / product_id.
 */
const migrateLegacyItem = (item) => {
  if (item.cart_item_id) return item;
  const product_id = item.product_id || item.id;
  const variant_id = item.variant_id || null;
  return {
    ...item,
    cart_item_id: cartKey(product_id, variant_id),
    product_id,
    variant_id,
    variant_name: item.variant_name || null,
    variant_slug: item.variant_slug || null,
    notes: item.notes ?? null,
  };
};

// ── Hook ───────────────────────────────────────────────────────────

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

// ── Provider ───────────────────────────────────────────────────────

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  // Charge le panier depuis localStorage au mount + migration
  useEffect(() => {
    const savedCart = localStorage.getItem("legers-cart");
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        setItems(Array.isArray(parsed) ? parsed.map(migrateLegacyItem) : []);
      } catch (e) {
        console.error("Erreur lors du chargement du panier:", e);
      }
    }
  }, []);

  // Sauvegarde le panier à chaque modification
  useEffect(() => {
    localStorage.setItem("legers-cart", JSON.stringify(items));
  }, [items]);

  /**
   * Ajoute un produit au panier.
   * @param product         Le produit (contient id, name, price, ...)
   * @param quantity        Quantité (défaut 1)
   * @param variantOrSource Variante (objet) OU source de tracking (string, backward compat)
   * @param source          Source de tracking si variant est passé
   */
  const addItem = (
    product,
    quantity = 1,
    variantOrSource = null,
    source = "unknown",
  ) => {
    // Backward compat : les anciens appels sont addItem(product, qty, 'source')
    let variant = null;
    let actualSource = source;
    if (typeof variantOrSource === "string") {
      actualSource = variantOrSource;
    } else if (variantOrSource && typeof variantOrSource === "object") {
      variant = variantOrSource;
    }

    const key = cartKey(product.id, variant?.id);

    setItems((prevItems) => {
      const existing = prevItems.find((i) => i.cart_item_id === key);
      if (existing) {
        return prevItems.map((i) =>
          i.cart_item_id === key
            ? { ...i, quantity: i.quantity + quantity }
            : i,
        );
      }
      return [...prevItems, buildCartItem(product, quantity, variant)];
    });

    // Tracking — inclut le nom de la variante dans le libellé si applicable
    const trackedPrice = variant?.price ?? product.price;
    const trackedProduct = variant
      ? { ...product, price: trackedPrice, item_variant: variant.name }
      : product;

    trackEvent("add_to_cart", {
      currency: "CHF",
      value: Number(trackedPrice || 0) * Number(quantity || 1),
      source: actualSource,
      items: [toGaItem(trackedProduct, quantity)],
    });
  };

  /**
   * Retire un item du panier.
   * @param cartItemId  L'identifiant unique de l'item (item.cart_item_id)
   * @param source      Source de tracking
   */
  const removeItem = (cartItemId, source = "unknown") => {
    const item = items.find((i) => i.cart_item_id === cartItemId);

    if (item) {
      trackEvent("remove_from_cart", {
        currency: "CHF",
        value: Number(item.price || 0) * Number(item.quantity || 1),
        source,
        items: [toGaItem(item, item.quantity || 1)],
      });
    }

    setItems((prevItems) =>
      prevItems.filter((i) => i.cart_item_id !== cartItemId),
    );
  };

  /**
   * Change la quantité d'un item.
   * @param cartItemId  L'identifiant unique de l'item
   * @param quantity    Nouvelle quantité (0 = supprime)
   * @param source      Source de tracking
   */
  const updateQuantity = (cartItemId, quantity, source = "unknown") => {
    const item = items.find((i) => i.cart_item_id === cartItemId);
    if (!item) return;

    if (quantity <= 0) {
      removeItem(cartItemId, source);
      return;
    }

    if (quantity > item.quantity) {
      trackEvent("add_to_cart", {
        currency: "CHF",
        value: Number(item.price || 0) * Number(quantity - item.quantity),
        source,
        items: [toGaItem(item, quantity - item.quantity)],
      });
    }

    if (quantity < item.quantity) {
      trackEvent("remove_from_cart", {
        currency: "CHF",
        value: Number(item.price || 0) * Number(item.quantity - quantity),
        source,
        items: [toGaItem(item, item.quantity - quantity)],
      });
    }

    setItems((prevItems) =>
      prevItems.map((i) =>
        i.cart_item_id === cartItemId ? { ...i, quantity } : i,
      ),
    );
  };

  /**
   * Change ou définit les notes d'un item (ex: "sans oignons").
   */
  const updateNotes = (cartItemId, notes) => {
    setItems((prevItems) =>
      prevItems.map((i) =>
        i.cart_item_id === cartItemId ? { ...i, notes } : i,
      ),
    );
  };

  const clearCart = (source = "unknown") => {
    if (items.length) {
      trackEvent("clear_cart", {
        currency: "CHF",
        value: items.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0,
        ),
        source,
        items: items.map((item) => toGaItem(item, item.quantity || 1)),
      });
    }

    setItems([]);
  };

  // Computed
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  const subtotal = items.reduce(
    (total, item) => total + item.price * item.quantity,
    0,
  );
  const total = subtotal;

  // UI controls
  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);
  const toggleCart = () => setIsOpen((prev) => !prev);

  return (
    <CartContext.Provider
      value={{
        items,
        itemCount,
        subtotal,
        total,
        isOpen,
        addItem,
        removeItem,
        updateQuantity,
        updateNotes,
        clearCart,
        openCart,
        closeCart,
        toggleCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;